import React, { useEffect, useState } from 'react';
import { FormFieldKind } from '../FormFieldGroup';
import { classNames } from '../../utils/helpers';
import styles from '../FormFieldGroup/FormField.module.scss';
import { Tooltip, TooltipPlacement } from '../index';
export var TextInputType;
(function (TextInputType) {
    TextInputType["Text"] = "text";
    TextInputType["Number"] = "number";
    TextInputType["Hidden"] = "hidden";
    TextInputType["Password"] = "password";
})(TextInputType || (TextInputType = {}));
const TextInput = ({ id, name = id, type = TextInputType.Text, label, placeholder, value, minValue = 0, maxValue = Number.MAX_SAFE_INTEGER, help, info, kind = FormFieldKind.Default, format, required = false, disabled = false, readOnly = false, skipValidation = false, autoFocus = false, disableAutocomplete = false, onChange = () => undefined, onValidChange = () => undefined, onAdditionalValidation = () => undefined, onFocus = () => undefined, }) => {
    const [internalValue, setInternalValue] = useState();
    const [internalError, setInternalError] = useState('');
    const [internalKind, setInternalKind] = useState(FormFieldKind.Default);
    const [isDirty, setDirty] = useState(false);
    const [isActive, setActive] = useState(false);
    const [hasError, setHasError] = useState(false);
    useEffect(() => {
        if (internalValue !== undefined && value !== internalValue) {
            onChange(id, internalValue);
        }
    }, [internalValue]);
    useEffect(() => {
        let nextKind = kind;
        if (!skipValidation && !disabled && isDirty) {
            if (isActive && !internalError) {
                nextKind = FormFieldKind.Approved;
            }
            else if (!isActive && !!internalError) {
                nextKind = FormFieldKind.Error;
            }
        }
        setInternalKind(nextKind);
    }, [skipValidation, disabled, isDirty, isActive, internalError]);
    useEffect(() => setInternalKind(kind), [kind]);
    const test = (targetValue = '', dirty = true) => {
        let errorMessage;
        if (!skipValidation) {
            if (required && targetValue === '') {
                errorMessage = required.toString();
            }
            else if (targetValue &&
                (format === null || format === void 0 ? void 0 : format.regex) &&
                (format === null || format === void 0 ? void 0 : format.error) &&
                !new RegExp(format.regex).test(targetValue)) {
                errorMessage = format.error;
            }
            else {
                errorMessage = onAdditionalValidation(targetValue, !errorMessage, id);
            }
        }
        setInternalError(errorMessage || '');
        setDirty(dirty);
        onValidChange(id, !errorMessage);
    };
    const parseValue = (targetValue = '') => {
        if (type === TextInputType.Number) {
            const parsedNumber = parseInt(targetValue, 10);
            if (Number.isNaN(parsedNumber)) {
                return '';
            }
            return `${Math.min(Math.max(parsedNumber, minValue), maxValue)}`;
        }
        return targetValue !== null && targetValue !== void 0 ? targetValue : '';
    };
    useEffect(() => {
        const newValue = parseValue(value);
        test(newValue, false);
        setInternalValue(newValue);
    }, [value]);
    const handleChange = (e) => {
        e.preventDefault();
        const newValue = parseValue(e.target.value);
        test(newValue);
        setInternalValue(newValue);
    };
    const handleFocusGain = (e) => {
        e.preventDefault();
        setDirty(true);
        setActive(true);
        onFocus(id);
    };
    const handleFocusLost = (e) => {
        e.preventDefault();
        setActive(false);
        test(internalValue, true);
    };
    const handleInvalid = (e) => {
        e.preventDefault();
        setDirty(!disabled);
    };
    const handleKeyPress = (e) => type === TextInputType.Number && !/[0-9]/.test(e.key) && e.preventDefault();
    const inputProps = {
        id,
        name,
        type,
        placeholder,
        value: internalValue || '',
        min: type === TextInputType.Number ? minValue : undefined,
        max: type === TextInputType.Number ? maxValue : undefined,
        required: !!required,
        pattern: format === null || format === void 0 ? void 0 : format.regex,
        disabled,
        readOnly,
        autoFocus,
        autoComplete: disableAutocomplete ? 'off' : undefined,
        onChange: handleChange,
        onFocus: handleFocusGain,
        onBlur: handleFocusLost,
        onInvalid: handleInvalid,
        onKeyPress: handleKeyPress,
    };
    useEffect(() => setHasError(internalKind === FormFieldKind.Error && !!internalError), [internalKind, internalError]);
    return (React.createElement("div", { className: classNames(styles.formFieldContainer, isActive && styles.active, type === 'hidden' && styles.hidden) },
        React.createElement("div", { className: styles.formField },
            React.createElement("div", { className: styles.formFieldLabelWrapper },
                React.createElement("label", { htmlFor: id, className: classNames((isActive || !!internalValue) && styles.collapsed, disabled && styles.disabled, internalKind === FormFieldKind.Error && styles.error) }, label),
                React.createElement("div", { className: classNames(styles.fade, disabled && styles.disabled, internalKind === FormFieldKind.Error && styles.error) })),
            React.createElement("input", { className: styles[internalKind], ...inputProps })),
        !!info && (React.createElement("div", { className: styles.infoContainer, "data-testid": `test-${id}-info-message` },
            React.createElement(Tooltip, { title: info, placement: TooltipPlacement.BOTTOM_END },
                React.createElement("div", { className: styles.info })))),
        (hasError || !!help) && (React.createElement("div", { className: classNames(styles.formFieldHelp, styles[internalKind]), "data-testid": `test-${id}-${internalKind}-message` }, hasError ? internalError : help))));
};
export default TextInput;
